import '../../../shared/style.css';

import React, { useMemo, useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { refreshCurrentLegacyPage } from '@economic/react-app';

import internalApiClient from '../../../../../../shared/util/api/internalApiClient';
import track from '../../../../../../shared/util/utilities/mixpanel';
import useLocalStorage from '../../../../../../shared/hooks/useLocalStorage';
import { bookAndSendTracker, splitEmails } from '../../utils';
import { BookDraftInvoiceDialog } from './BookDraftInvoiceDialog';
import { emailOptionsFromTemplates } from '../../utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../utils/emailTemplateFormState';
import { GetInformationInvoiceResult, InvoiceBookAndSendRequest, SendByType, BookAndSendFormData } from '../types';
import { InvoiceLayoutType } from '../../../../../../../types/internal-api-extra';
import { tBB, useLocalization } from '../../../../../../shared/components/l10n/l10n';
import { transformEmailOptions } from '../../utils/transformEmailOptions';
import MoneyflowPostSaleDialog from '../moneyflow/MoneyflowPostSaleDialog';
import { MoneyflowOfferOverviewResult } from '../../../../../../../types/internal-api';
import { useDialogManagerContext } from '../../../../../../shared/components/DialogManagerContext';
import { useEmailTemplates } from '../../utils/useEmailTemplates';
import { usePostQuery } from '../../../../../../shared/hooks/usePostQuery';

async function bookDraftInvoice(request: InvoiceBookAndSendRequest): Promise<any> {
    return await internalApiClient.post(`/sales/invoice/BookAndSend`, request);
}

function BookDraftInvoiceDialogContainer({ onBooked }: { onBooked: (invoiceId: number) => void }) {
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { draftId } = location.state || ({} as any);
    const [skipBookOnlyConfirmation, setSkipBookOnlyConfirmation] = useLocalStorage<boolean>('skipBookOnlyConfirmation');
    const [includePaymentLink, setIncludePaymentLink] = useLocalStorage<boolean>('includePaymentLink');

    const [booking, setBooking] = useState<boolean>(false);
    const [persistedInformation, setPersistedInformation] = useState<Partial<BookAndSendFormData> | null>(null);

    const {
        loading,
        data: information,
        repost: reloadInformation,
    } = usePostQuery<GetInformationInvoiceResult>(
        `/sales/invoice/GetInformation`,
        {
            invoiceDraftId: draftId,
        },
        !draftId
    );

    const type = information ? (information.isCreditNote ? InvoiceLayoutType.CreditNote : InvoiceLayoutType.Invoice) : undefined;

    const { data: templates, loading: templatesLoading } = useEmailTemplates(type);

    useEffect(() => {
        bookAndSendTracker('popup.open');
    }, []);

    const loadingTexts = useLocalization(textIds);

    const transformedInformation = useMemo(() => {
        if (!information) {
            return undefined;
        }

        const dataWithTranformedEmailOptions = transformEmailOptions(information);

        return {
            ...dataWithTranformedEmailOptions,
            moneyflowEinvoiceEmail: 0,
            skipBookOnlyConfirmation,
            includePaymentLink,
            emailOptions: {
                ...dataWithTranformedEmailOptions.emailOptions,
                ...emailOptionsFromTemplates(templates),
            },
            ...emailTemplateFormState(templates),
            ...persistedInformation,
        };
    }, [information, templates, persistedInformation]);

    const { pushDialog } = useDialogManagerContext();

    function showMoneyflowPostSaleDialog(moneyflowOffer: MoneyflowOfferOverviewResult) {
        const dialog = <MoneyflowPostSaleDialog offer={moneyflowOffer} />;
        pushDialog(dialog);
    }

    async function toggleMoneyflow(isToggled: boolean, formValues: BookAndSendFormData) {
        track('bookandsend', 'moneyflow.toggled', { toggled: isToggled });
        await internalApiClient.post('/moneyflow/offer/toggle', { invoiceDraftId: draftId, isToggled });
        refreshCurrentLegacyPage('content');

        // ensure email information are not cleared after reload
        setPersistedInformation({ emailOptions: formValues.emailOptions });
        reloadInformation();
    }

    async function book(values: BookAndSendFormData) {
        let recipients = splitEmails(values.emailOptions.recipients);
        let recipientsCc = splitEmails(values.emailOptions.recipientsCc);
        let recipientsBcc = splitEmails(values.emailOptions.recipientsBcc);

        if (
            values.sendByType === SendByType.oioxml &&
            information?.moneyflowOffer?.toggled &&
            values.moneyflowEinvoiceEmails.length > 0
        ) {
            recipients = [values.moneyflowEinvoiceEmails[values.moneyflowEinvoiceEmail]];
            recipientsCc = [];
            recipientsBcc = [];
        }

        const requestData: InvoiceBookAndSendRequest = {
            invoiceDraftId: draftId,
            sendByType: values.sendByType,
            showPdf: values.emailOptions.showPdf,
            includePaymentLink: values.includePaymentLink,
            emailOptions: {
                subject: values.emailOptions.subject,
                body: values.emailOptions.body,
                encrypt: values.emailOptions.encrypt,
                includePdfLink: values.emailOptions.includePdfLink,
                includePdfAsAttachment: values.emailOptions.includePdfAsAttachment,
                includeOioLink: values.emailOptions.includeOioLink,
                includeOioAsAttachment: values.emailOptions.includeOioAsAttachment,
                recipients,
                recipientsCc,
                recipientsBcc,
                saveSubject: values.emailOptions.saveSubject,
                saveBody: values.emailOptions.saveBody,
            },
        };

        setBooking(true);
        const result = await bookDraftInvoice(requestData);
        setBooking(false);

        if (!result) {
            return;
        }

        if (result.data.documentId > 0) {
            const goToLink = values.sendByType === SendByType.oioxml ? '/sales/invoicing/sent' : '/sales/invoicing/archive';

            let successMessage;
            if (result.data.electronicInvoiceCopyRecipients && result.data.electronicInvoiceCopyRecipients.length > 0) {
                successMessage = tBB('TOAST_INVOICE_BOOK_AND_SEND_WITH_CONTACTS_SUCCESS', {
                    CONTACTS: (
                        <ul>
                            {result.data.electronicInvoiceCopyRecipients.map((contact, index) => (
                                <li key={index}>{contact}</li>
                            ))}
                        </ul>
                    ),
                });
            } else {
                successMessage = tBB('TOAST_INVOICE_BOOK_AND_SEND_SUCCESS', {
                    __LINK: content => (
                        <Link to={goToLink} onClick={() => bookAndSendTracker('toastlink.open')}>
                            {content}
                        </Link>
                    ),
                });
            }

            toast.success(successMessage);

            setIncludePaymentLink(values.includePaymentLink);
            setSkipBookOnlyConfirmation(values.skipBookOnlyConfirmation);

            if (values.emailOptions.showPdf && result.data.pdfLink) {
                window.top?.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
            }

            onBooked(result.data.documentId);

            if (information?.moneyflowOffer?.toggled) {
                showMoneyflowPostSaleDialog(information.moneyflowOffer);
            }
        } else {
            let errorMessage = '';
            if (result.data.messages.length > 0) {
                errorMessage = result.data.messages.map(m => `${m.message}`).join(', ');
            }

            toast.error(errorMessage);
        }
    }

    function hide() {
        bookAndSendTracker('popup.closed');
        navigate(location.pathname + location.search);
    }

    if (!draftId) {
        return null;
    }

    return (
        <BookDraftInvoiceDialog
            loading={loading || loadingTexts || templatesLoading || booking}
            information={transformedInformation}
            onBook={book}
            onHide={hide}
            onToggleMoneyflow={toggleMoneyflow}
        />
    );
}

const textIds = [
    'FORM_BOOKANDSEND_BACK',
    'FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_DO_NOT_SHOW',
    'FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_TEXT',
    'FORM_BOOKANDSEND_BOOK_TEXT',
    'FORM_BOOKANDSEND_CANCEL',
    'FORM_BOOKANDSEND_DOCUMENT_TYPE_CREDIT_NOTE',
    'FORM_BOOKANDSEND_DOCUMENT_TYPE_DRAFT_INVOICE',
    'FORM_BOOKANDSEND_ELECTRONIC_SINGLE_INVOICE_INFO',
    'FORM_BOOKANDSEND_ELECTRONIC_SINGLE_INVOICE_INFO_URL',
    'FORM_BOOKANDSEND_ELECTRONIC_SINGLE_INVOICE_TEXT',
    'FORM_BOOKANDSEND_EINVOICE_INSTANTPAYMENT_TITLE',
    'FORM_BOOKANDSEND_EINVOICE_INSTANTPAYMENT_TEXT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_LINK',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_EMAIL_INCLUDE_PAYMENT_LINK',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_SINGLE_INVOICE_TEXT',
    'FORM_BOOKANDSEND_ERRORS_HEADER_CANNOTBOOK',
    'FORM_BOOKANDSEND_ERROR_NOTIFICATION_MESSAGE',
    'TOAST_INVOICE_BOOK_AND_SEND_WITH_CONTACTS_SUCCESS',
    'FORM_BOOKANDSEND_INVOICE_HEADER',
    'FORM_BOOKANDSEND_INVOICE_MONEYFLOW_HEADER',
    'FORM_BOOKANDSEND_MONEYFLOW_AMOUNT_RECEIVED',
    'FORM_BOOKANDSEND_MONEYFLOW_ENABLED_INFO',
    'FORM_BOOKANDSEND_MONEYFLOW_ENABLED_TEXT',
    'FORM_BOOKANDSEND_MONEYFLOW_FEE',
    'FORM_BOOKANDSEND_MONEYFLOW_FEE_DISCOUNT',
    'FORM_BOOKANDSEND_MONEYFLOW_NET_AMOUNT',
    'FORM_BOOKANDSEND_MONEYFLOW_PAYMENTOVERVIEW',
    'FORM_BOOKANDSEND_MONEYFLOW_VAT',
    'FORM_BOOKANDSEND_MONEYFLOW_VIEWAGREEMENT',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'FORM_BOOKANDSEND_SEND_TEXT',
    'FORM_BOOKANDSEND_SHOW_INVOICE',
    'TOAST_INVOICE_BOOK_AND_SEND_SUCCESS',
    'FORM_FIELD_LABEL_REQUIRED',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'SHARED_FORM_OK',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_BOOKANDSEND_ERROR_NOTIFICATION_HEADER',
    'FORM_BOOKANDSEND_CHOOSEEMAILTEMPLATE',
    'FORM_BOOKANDSEND_CONVERT_TO_EINVOICE',
    'FORM_BOOKANDSEND_CONVERT_TO_EMAIL',
    'FORM_BOOKANDSEND_MONEYFLOW_SELECTEMAIL_TITLE',
    'FORM_BOOKANDSEND_MONEYFLOW_SELECTEMAIL_TEXT',
    'FORM_BOOKANDSEND_MONEYFLOW_SELECTEMAIL',
    'FORM_BOOKANDSEND_MONEYFLOW_TOGGLE',
];

export default BookDraftInvoiceDialogContainer;
